import React from 'react';
import styles from './IngredientDietaryAttributes.module.css';
import { getDietaryTooltip } from '../../utils/dietaryTooltips';

interface IngredientDietaryAttributesProps {
  ingredient: {
    name: string;
    dietaryAttributes?: {
      vegan?: boolean;
      vegetarian?: boolean;
      pescatarian?: boolean;
      gluten_free?: boolean;
      dairy_free?: boolean;
      nut_free?: boolean;
      egg_free?: boolean;
      soy_free?: boolean;
      shellfish_free?: boolean;
      fish_free?: boolean;
      sesame_free?: boolean;
      low_fodmap?: boolean;
      keto?: boolean;
      paleo?: boolean;
      atkins?: boolean;
      corn_free?: boolean;
      mustard_free?: boolean;
    };
    healthConcerns?: string[];
    conflicts?: string[];
    matches?: string[];
  };
  userPreferences?: {
    vegan?: boolean;
    vegetarian?: boolean;
    pescatarian?: boolean;
    gluten_free?: boolean;
    dairy_free?: boolean;
    nut_free?: boolean;
    egg_free?: boolean;
    soy_free?: boolean;
    shellfish_free?: boolean;
    fish_free?: boolean;
    sesame_free?: boolean;
    low_fodmap?: boolean;
    keto?: boolean;
    paleo?: boolean;
    atkins?: boolean;
    corn_free?: boolean;
    mustard_free?: boolean;
  };
  userHealthConcerns?: Array<{
    name: string;
    type: 'min' | 'max';
    value: number;
  }>;
  variant?: 'default' | 'compact' | 'inline';
  showOnlyMatches?: boolean;
  showConflicts?: boolean;
  showHealthConcerns?: boolean;
  maxTags?: number;
  className?: string;
}

const IngredientDietaryAttributes: React.FC<IngredientDietaryAttributesProps> = ({
  ingredient,
  userPreferences,
  userHealthConcerns,
  variant = 'default',
  showOnlyMatches = true,
  showConflicts = true,
  showHealthConcerns = true,
  maxTags,
  className
}) => {

  if (!ingredient.dietaryAttributes && !ingredient.healthConcerns && !ingredient.conflicts && !ingredient.matches) return null;

  const dietaryMapping = {
    'vegan': 'Vegan',
    'vegetarian': 'Vegetarian',
    'pescatarian': 'Pescatarian',
    'gluten_free': 'Gluten Allergy',
    'dairy_free': 'Dairy Allergy',
    'nut_free': 'Nut Allergy',
    'egg_free': 'Egg Allergy',
    'soy_free': 'Soy Allergy',
    'shellfish_free': 'Shellfish Allergy',
    'fish_free': 'Fish Allergy',
    'sesame_free': 'Sesame Allergy',
    'low_fodmap': 'Low FODMAP',
    'keto': 'Keto',
    'paleo': 'Paleo',
    'atkins': 'Atkins',
    'corn_free': 'Corn Allergy',
    'mustard_free': 'Mustard Allergy'
  };

  const healthConcernMapping = {
    'high_protein_needs': 'High Protein',
    'low_sodium_needs': 'Low Sodium',
    'high_fiber_needs': 'High Fiber',
    'low_sugar_needs': 'Low Sugar',
    'high_iron_needs': 'High Iron',
    'high_calcium_needs': 'High Calcium',
    'high_omega3_needs': 'High Omega-3'
  };

  // Collect all types of highlights
  const allHighlights: Array<{
    key: string;
    label: string;
    type: 'dietary_match' | 'dietary_conflict' | 'health_benefit' | 'health_concern';
    isUserPreference: boolean;
    tooltip: string;
  }> = [];

  // 1. Dietary matches (ingredient matches user preferences)
  if (ingredient.dietaryAttributes && userPreferences) {
    // Only process ingredients with confirmed dietary attributes (true values)
    const confirmedAttributes = Object.entries(ingredient.dietaryAttributes)
      .filter(([key, value]) => value === true);
    
    if (confirmedAttributes.length === 0) {
      // No confirmed dietary attributes, don't show any tags
      return null;
    }
    
    confirmedAttributes.forEach(([key, value]) => {
      const isUserPreference = userPreferences[key as keyof typeof userPreferences] === true;
      
      if (isUserPreference) {
        allHighlights.push({
          key,
          label: dietaryMapping[key as keyof typeof dietaryMapping],
          type: 'dietary_match',
          isUserPreference: true,
          tooltip: getDietaryTooltip(key, true, true)
        });
      }
    });
  }

  // 2. Dietary conflicts (ingredient conflicts with user preferences)
  if (showConflicts && ingredient.dietaryAttributes && userPreferences) {
    // Only check conflicts for ingredients that have confirmed dietary attributes
    const confirmedAttributes = Object.entries(ingredient.dietaryAttributes)
      .filter(([key, value]) => value === true);
    
    if (confirmedAttributes.length === 0) {
      // No confirmed dietary attributes, no conflicts to check
      return null;
    }
    
    // Check each user preference against ingredient attributes
    Object.entries(userPreferences)
      .filter(([key, value]) => value === true) // User wants this attribute
      .forEach(([key, value]) => {
        const ingredientHasAttribute = ingredient.dietaryAttributes?.[key as keyof typeof ingredient.dietaryAttributes];
        
        // Conflict: User wants this attribute but ingredient explicitly doesn't have it (false)
        if (ingredientHasAttribute === false) {
          allHighlights.push({
            key,
            label: dietaryMapping[key as keyof typeof dietaryMapping],
            type: 'dietary_conflict',
            isUserPreference: true,
            tooltip: getDietaryTooltip(key, true, false)
          });
        }
      });
  }

  // 3. Health benefits (ingredient matches user health concerns)
  if (showHealthConcerns && ingredient.healthConcerns && userHealthConcerns) {
    ingredient.healthConcerns.forEach(concern => {
      const userHasConcern = userHealthConcerns.some(uc => uc.name === concern);
      if (userHasConcern) {
        allHighlights.push({
          key: concern,
          label: healthConcernMapping[concern as keyof typeof healthConcernMapping] || concern,
          type: 'health_benefit',
          isUserPreference: true,
          tooltip: `${concern.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} - This ingredient supports your health goals`
        });
      }
    });
  }


  if (allHighlights.length === 0) return null;

  // Apply max tags limit
  const finalHighlights = maxTags 
    ? allHighlights.slice(0, maxTags)
    : allHighlights;

  const remainingCount = maxTags && allHighlights.length > maxTags
    ? allHighlights.length - maxTags
    : 0;

  return (
    <div className={`${styles.container} ${styles[variant]} ${className || ''}`}>
      {finalHighlights.map(({ key, label, type, isUserPreference, tooltip }) => (
        <span
          key={`${type}-${key}`}
          className={`${styles.dietaryTag} ${
            type === 'dietary_match' ? styles.userMatch :
            type === 'dietary_conflict' ? styles.userConflict :
            type === 'health_benefit' ? styles.healthBenefit :
            styles.ingredientAttribute
          } ${styles[key]}`}
          title={tooltip}
        >
          {label}
        </span>
      ))}
      {remainingCount > 0 && (
        <span className={styles.moreTags}>
          +{remainingCount} more
        </span>
      )}
    </div>
  );
};

export default IngredientDietaryAttributes;
